import pluralize from 'pluralize';
import { type ChangeEvent, type KeyboardEvent, type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';

import { useTrackShareClicked } from '@air/analytics';
import { Skeleton } from '@air/component-skeleton';
import { Link, Plus, Search } from '@air/next-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { Input } from '@air/primitive-input';
import { useAirModal } from '@air/provider-modal';
import { CustomToastItem, useToasts } from '@air/provider-toast';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { CreateSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/CreateSharePrivateCollectionModal';
import { ShareLinkActionConfirmationToastContent } from '~/components/SharePrivateCollectionModal/components/ShareLinkActionConfirmationToastContent';
import { useAddCollectionItemsMutation } from '~/components/SharePrivateCollectionModal/hooks/mutations/useAddCollectionItemsMutation';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useListShortUrls } from '~/swr-hooks/shortUrl/useListShortUrls';
import { canCreateCollection, canEditCollection } from '~/utils/permissions/workspacePermissions';
import { getFullShareUrl } from '~/utils/shareUrl';

export type GetShareLinkMenuOptionsParams = {
  assetIds: string[];
  boardIds: string[];
};

const SHARE_LINK_MENU_LIMIT = 5;

export const useGetPrivateShareLinkMenuOptions = () => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 200, {
    trailing: true,
  });
  const [showCreatePrivateCollectionModal] = useAirModal(CreateSharePrivateCollectionModal);
  const { mutate: addCollectionItems } = useAddCollectionItemsMutation();
  const { showToast } = useToasts();
  const { trackShareClicked } = useTrackShareClicked();

  const { data: shortUrlsData, isFetching } = useListShortUrls({
    workspaceId: currentWorkspace?.id,
    limit: SHARE_LINK_MENU_LIMIT,
    orderBy: {
      field: 'createdAt',
      direction: 'desc',
    },
    search: debouncedSearch || undefined,
  });

  const onSearchInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  // Required to allow the search input to be focused when clicking on the search input when displayed in a drop-down menu
  // in *some* contexts, such as when triggered from Table View.
  const stopPropagation = useCallback((e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  const onAddToShareLink = useCallback(
    (shortUrlId: string, collectionId: string, { assetIds, boardIds }: GetShareLinkMenuOptionsParams) => {
      if (currentWorkspace?.id) {
        addCollectionItems(
          { workspaceId: currentWorkspace.id, collectionId, assetIds, boardIds },
          {
            onSuccess: () => {
              const totalItems = assetIds.length + boardIds.length;
              const shareUrl = getFullShareUrl(shortUrlId);

              showToast(
                ({ onDismiss }) => (
                  <ShareLinkActionConfirmationToastContent
                    message={`${pluralize('item', totalItems, true)} added to share link`}
                    shareUrl={shareUrl}
                    onDismiss={onDismiss}
                  />
                ),
                {
                  timeBeforeAutoDismiss: 5_000,
                  CustomContainer: CustomToastItem,
                },
              );

              dispatch(resetSelectedItemsAction());
            },
          },
        );
      }
    },
    [addCollectionItems, currentWorkspace?.id, dispatch, showToast],
  );

  const searchShareLinksOptions = useMemo<DropdownMenuOption[]>(
    () => [
      {
        id: 'share-link-search',
        type: 'children',
        children: (
          <div className="flex flex-col">
            <div className="p-2 font-bold text-10 text-grey-10 uppercase tracking-[0.4px]">
              Add to existing share links
            </div>
            <div className="relative">
              <Search className="absolute top-1/2 left-2 size-4 -translate-y-1/2 text-grey-9" />
              <Input
                className="w-full rounded-8 pr-2 pl-7"
                placeholder={shortUrlsData?.total ? `Search ${shortUrlsData.total} share links` : 'Search share links'}
                value={search}
                onChange={onSearchInputChange}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onKeyDown={stopPropagation}
                size="large"
              />
            </div>
          </div>
        ),
      },
      {
        id: 'share-link-search-separator',
        type: 'separator',
      },
    ],
    [shortUrlsData?.total, search, onSearchInputChange, stopPropagation],
  );

  const getExistingShareLinksOptions = useCallback(
    ({ assetIds, boardIds }: GetShareLinkMenuOptionsParams): DropdownMenuOption[] => {
      const options: DropdownMenuOption[] = [];

      if (isFetching) {
        for (let i = 0; i < SHARE_LINK_MENU_LIMIT; i++) {
          options.push({
            id: `skeleton-${i}`,
            label: <Skeleton className="h-4" height={16} width={120} />,
            disabled: true,
            type: 'item',
            prefix: <Skeleton className="size-4" height={16} width={16} />,
          });
        }
      } else if (shortUrlsData?.data && shortUrlsData.data.length > 0) {
        for (const shortUrl of shortUrlsData.data) {
          const { collection } = shortUrl;
          options.push({
            id: collection.id,
            label: collection.name,
            onSelect: () => {
              onAddToShareLink(shortUrl.id, collection.id, { assetIds, boardIds });
            },
            type: 'item',
            prefix: <Link className="size-4 text-grey-10" />,
          });
        }
      } else if (debouncedSearch.trim().length > 0) {
        options.push({
          id: 'no-search-results',
          label: 'No search results',
          disabled: true,
          type: 'item',
        });
      } else if (shortUrlsData?.data && shortUrlsData.data.length === 0) {
        options.push({
          id: 'no-share-links',
          label: 'No share links',
          disabled: true,
          type: 'item',
        });
      }

      options.push({
        id: 'new-share-link-separator',
        type: 'separator',
      });

      return options;
    },
    [shortUrlsData?.data, onAddToShareLink, debouncedSearch, isFetching],
  );

  const getNewShareLinkOptions = useCallback(
    ({ assetIds, boardIds }: GetShareLinkMenuOptionsParams): DropdownMenuOption[] => [
      {
        id: 'new-share-link',
        label: 'New share link',
        prefix: <Plus className="size-4 text-grey-10" />,
        onSelect: () => {
          trackShareClicked({ location: 'context_menu' });
          showCreatePrivateCollectionModal({ assetIds, boardIds });
        },
        type: 'item',
      },
    ],
    [showCreatePrivateCollectionModal, trackShareClicked],
  );

  const getShareLinkMenuOptions = useCallback(
    ({ assetIds, boardIds }: GetShareLinkMenuOptionsParams): DropdownMenuOption[] => {
      const options: DropdownMenuOption[] = [];

      if (canEditCollection(workspacePermissions)) {
        options.push(...searchShareLinksOptions);
        options.push(...getExistingShareLinksOptions({ assetIds, boardIds }));
      }

      if (canCreateCollection(workspacePermissions)) {
        options.push(...getNewShareLinkOptions({ assetIds, boardIds }));
      }

      return options;
    },
    [searchShareLinksOptions, getExistingShareLinksOptions, getNewShareLinkOptions, workspacePermissions],
  );

  return {
    getShareLinkMenuOptions,
  };
};
